import { useState } from 'react';
import { ApiError, api } from '../api';
import { Banner, Field, JsonEditor, Pill, Spinner, useAsync } from '../components/Common';
import { relativeTime } from '../format';

interface Connection {
  id: number;
  slug: string;
  name: string;
  kind: 'mcp_http' | 'mcp_sse' | 'mcp_stdio' | 'http' | 'process' | 'dialpad';
  base_url: string | null;
  enabled: boolean;
  notes: string | null;
  config?: Record<string, any>;
  credentialSummary: { bearer: string | null; headerValue: string | null; basicUser: string | null; undecryptable: boolean };
  tools: { name: string; description?: string; inputSchema?: any }[];
  tools_synced_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
}

const KIND_LABELS: Record<string, string> = {
  process: 'Local script (Python or PowerShell on the reporting host)',
  http: 'REST / webhook (any JSON API)',
  dialpad: 'Dialpad (Stats API — submits, polls and downloads the export for you)',
  mcp_http: 'MCP over HTTP (n8n MCP Server Trigger — Production URL)',
  mcp_sse: 'MCP over SSE (older n8n trigger — /sse URL)',
  mcp_stdio: 'MCP over stdio (local MCP server process)',
};

/** Kinds that run something locally rather than calling a URL. */
const LOCAL_KINDS = new Set(['process', 'mcp_stdio']);

/**
 * Entry points.
 *
 * "Edit entry points" from the brief. An entry point is an addressable upstream
 * plus its credential; a request is what you call on it. Keeping them separate is
 * what lets a token be rotated once instead of in fifteen places.
 */
export function Connections({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useAsync(() => api.get<Connection[]>('/api/connections'));
  const [editing, setEditing] = useState<Connection | 'new' | null>(null);
  const [testResult, setTestResult] = useState<Record<number, { ok: boolean; detail: string }>>({});
  const [busyId, setBusyId] = useState<number | null>(null);

  const test = async (conn: Connection) => {
    setBusyId(conn.id);
    try {
      const result = await api.post<{ ok: boolean; detail: string }>(`/api/connections/${conn.id}/test`);
      setTestResult((prev) => ({ ...prev, [conn.id]: result }));
    } catch (err) {
      setTestResult((prev) => ({ ...prev, [conn.id]: { ok: false, detail: err instanceof ApiError ? err.message : String(err) } }));
    } finally {
      setBusyId(null);
    }
  };

  const syncTools = async (conn: Connection) => {
    setBusyId(conn.id);
    try {
      const { tools } = await api.post<{ tools: any[] }>(`/api/connections/${conn.id}/sync-tools`);
      setTestResult((prev) => ({ ...prev, [conn.id]: { ok: true, detail: `Discovered ${tools.length} tool(s).` } }));
      reload();
    } catch (err) {
      setTestResult((prev) => ({ ...prev, [conn.id]: { ok: false, detail: err instanceof ApiError ? err.message : String(err) } }));
    } finally {
      setBusyId(null);
    }
  };

  if (loading && !data) return <Spinner label="Loading entry points…" />;
  if (error) return <Banner tone="error">{error}</Banner>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Entry points</h1>
          <p>
            Where the platform gets its data. An MCP entry point discovers its own tools; a REST entry point is described
            per-endpoint on each request. Credentials are stored encrypted and never shown again after saving.
          </p>
        </div>
        {isAdmin && (
          <div className="page-actions">
            <button className="primary" onClick={() => setEditing('new')}>
              Add entry point
            </button>
          </div>
        )}
      </div>

      {!isAdmin && (
        <Banner tone="info">
          You can test entry points and refresh their tool lists. Editing URLs and credentials needs an administrator.
        </Banner>
      )}

      {(data ?? []).map((conn) => {
        const result = testResult[conn.id];
        return (
          <div className="card" key={conn.id} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ marginBottom: 4 }}>
                  {conn.name}{' '}
                  {conn.enabled ? <Pill tone="ok">enabled</Pill> : <Pill tone="off">disabled</Pill>}{' '}
                  {conn.config?.syncOnly && (
                    <>
                      <Pill tone="warn">scheduled sync only</Pill>{' '}
                    </>
                  )}
                  {conn.last_error && <Pill tone="bad">last call failed</Pill>}
                </h3>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
                  <span className="mono">{conn.slug}</span> · {KIND_LABELS[conn.kind] ?? conn.kind}
                </p>
                {conn.base_url && (
                  <p style={{ margin: '4px 0 0', fontSize: 12 }} className="mono">
                    {conn.base_url}
                  </p>
                )}
              </div>
              <div className="page-actions">
                <button className="small" onClick={() => test(conn)} disabled={busyId === conn.id}>
                  {busyId === conn.id ? 'Testing…' : 'Test'}
                </button>
                {conn.kind !== 'http' && (
                  <button className="small" onClick={() => syncTools(conn)} disabled={busyId === conn.id}>
                    Refresh tools
                  </button>
                )}
                {isAdmin && (
                  <button className="small" onClick={() => setEditing(conn)}>
                    Edit
                  </button>
                )}
              </div>
            </div>

            {conn.credentialSummary.undecryptable && (
              <Banner tone="error" title="Credential cannot be decrypted. ">
                APP_KEY has changed since this was saved. Re-enter the credential.
              </Banner>
            )}

            {conn.notes && <p className="note" style={{ marginTop: 10 }}>{conn.notes}</p>}

            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10, fontSize: 12, color: 'var(--ink-2)' }}>
              <span>
                Credential:{' '}
                {conn.credentialSummary.bearer ? (
                  <span className="mono">Bearer {conn.credentialSummary.bearer}</span>
                ) : conn.credentialSummary.headerValue ? (
                  <span className="mono">header {conn.credentialSummary.headerValue}</span>
                ) : conn.credentialSummary.basicUser ? (
                  <span className="mono">basic {conn.credentialSummary.basicUser}</span>
                ) : (
                  <Pill tone="warn">none set</Pill>
                )}
              </span>
              <span>Last success: {relativeTime(conn.last_ok_at)}</span>
              {conn.kind !== 'http' && (
                <span>
                  Tools: {conn.tools.length}{' '}
                  {conn.tools_synced_at ? `(synced ${relativeTime(conn.tools_synced_at)})` : '(never synced)'}
                </span>
              )}
            </div>

            {result && (
              <div style={{ marginTop: 12 }}>
                <Banner tone={result.ok ? 'good' : 'error'}>{result.detail}</Banner>
              </div>
            )}

            {conn.last_error && !result && (
              <p style={{ marginTop: 10, fontSize: 12, color: 'var(--critical)' }}>Last error: {conn.last_error}</p>
            )}

            {conn.tools.length > 0 && <ToolList tools={conn.tools} />}
          </div>
        );
      })}

      {editing && (
        <ConnectionEditor
          connection={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function ToolList({ tools }: { tools: { name: string; description?: string; inputSchema?: any }[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 12 }}>
      <button className="ghost small" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? '▾ Hide discovered tools' : `▸ Show ${tools.length} discovered tool(s)`}
      </button>
      {open && (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="grid">
            <thead>
              <tr>
                <th>Tool</th>
                <th>Description</th>
                <th>Arguments</th>
              </tr>
            </thead>
            <tbody>
              {tools.map((tool) => (
                <tr key={tool.name}>
                  <td className="mono">{tool.name}</td>
                  <td style={{ maxWidth: 320 }}>{tool.description ?? '—'}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {tool.inputSchema?.properties
                      ? Object.entries(tool.inputSchema.properties)
                          .map(([key, schema]: [string, any]) => `${key}: ${schema?.type ?? 'any'}`)
                          .join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="note">
            These names and argument shapes come from the live server. Use them exactly when building a request — the
            platform always sends explicit arguments and never lets a model guess them.
          </p>
        </div>
      )}
    </div>
  );
}

function ConnectionEditor({
  connection,
  onClose,
  onSaved,
}: {
  connection: Connection | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: connection?.name ?? '',
    kind: connection?.kind ?? 'mcp_http',
    base_url: connection?.base_url ?? '',
    notes: connection?.notes ?? '',
    enabled: connection?.enabled ?? false,
  });
  const [config, setConfig] = useState<Record<string, any>>(connection?.config ?? { timeoutMs: 60000, retries: 2, maxConcurrent: 4 });
  const [configValid, setConfigValid] = useState(true);
  const [bearer, setBearer] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!configValid) {
      setError('Fix the JSON in Advanced settings before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const secrets: Record<string, any> = {};
      if (bearer.trim()) secrets.bearer = bearer.trim();
      if (headerValue.trim()) secrets.headerValue = headerValue.trim();
      const payload = { ...form, config, secrets };
      if (connection) await api.put(`/api/connections/${connection.id}`, payload);
      else await api.post('/api/connections', payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 18, borderColor: 'var(--brand)' }}>
      <h3>{connection ? `Edit ${connection.name}` : 'New entry point'}</h3>
      {error && <Banner tone="error">{error}</Banner>}

      <div className="row">
        <Field label="Name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Type">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as Connection['kind'] })}>
            {Object.entries(KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {!LOCAL_KINDS.has(form.kind) && (
        <Field
          label="URL"
          help={
            form.kind === 'mcp_http'
              ? 'The Production URL from the n8n MCP Server Trigger node. The workflow must be published — a test URL only responds while the n8n editor is listening.'
              : form.kind === 'mcp_sse'
                ? 'The /sse URL from an older n8n MCP trigger.'
                : 'Base URL. Individual paths are set on each request, e.g. dialpad/callStats.'
          }
        >
          <input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://…" />
        </Field>
      )}

      <div className="row">
        <Field
          label={LOCAL_KINDS.has(form.kind) ? 'Secret (passed to the script as $env:BULLHORN_TOKEN)' : 'Bearer token'}
          help={
            connection
              ? 'Leave blank to keep the existing value.'
              : LOCAL_KINDS.has(form.kind)
                ? 'Stored encrypted and injected as an environment variable — never as a command-line argument, which any local user could read from the process list.'
                : 'The Bearer Auth credential you set on the MCP trigger.'
          }
        >
          <input type="password" value={bearer} onChange={(e) => setBearer(e.target.value)} placeholder="••••••••" autoComplete="off" />
        </Field>
        {form.kind === 'http' && (
          <Field label="Shared-secret header value" help={`Sent as the header named in config.authHeaderName.`}>
            <input type="password" value={headerValue} onChange={(e) => setHeaderValue(e.target.value)} autoComplete="off" />
          </Field>
        )}
      </div>

      {LOCAL_KINDS.has(form.kind) && (
        <div className="banner info" style={{ marginBottom: 14 }}>
          <span className="icon" aria-hidden>
            i
          </span>
          <div>
            This entry point <strong>runs a program on the reporting host</strong>. Set <code>command</code> and{' '}
            <code>args</code> in Advanced settings below — for the Python Bullhorn tool that is typically{' '}
            <code>{'{"command":"py","args":[],"cwd":"C:\\\\Bullhorn","output":"json","argStyle":"named"}'}</code>, with the
            script path given on each request. Report parameters are passed as separate arguments and are never
            interpreted by a shell.
          </div>
        </div>
      )}

      <JsonEditor
        label="Advanced settings"
        help={
          LOCAL_KINDS.has(form.kind)
            ? 'command, args, cwd, env, timeoutMs, maxConcurrent, argStyle (named | json-stdin | positional), output (json | json-lines | csv | text), failOnStderr. Keep maxConcurrent low — each call starts a new process.'
            : 'timeoutMs, retries, maxConcurrent, headers, query. maxConcurrent is worth keeping low for Bullhorn — a full OAuth login happens on every call.'
        }
        value={config}
        rows={7}
        onChange={(parsed, valid) => {
          setConfigValid(valid);
          if (valid) setConfig(parsed ?? {});
        }}
      />

      <Field label="Notes" help="What this connects to, and anything the next person needs to know.">
        <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 14 }}>
        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        Enabled — reports may call this entry point
      </label>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
